/**
 * The parts every redesigned screen is built from, top to bottom
 * (UI round of 2026-11-02, build decisions §1): the title row, the
 * one-line summary, the Needs attention strip, tabs, a search-and-filter
 * row, and the two-line status cell. The sortable table is `DataTable`.
 *
 * They hold no state of their own beyond what a tab bar's keyboard needs:
 * each screen decides what its summary says, what needs attention and
 * which tab is open.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { StatusChip } from './StatusChip';
import type { StatusKind } from '../tokens';
import './ScreenParts.css';

/** The title row: the `h1`, and the screen's one or two global actions. */
export function ScreenTitleRow({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="nx-screen-title">
      <h1 className="nx-section-title nx-screen-title__h1">{title}</h1>
      {children && <div className="nx-screen-title__actions">{children}</div>}
    </div>
  );
}

export type SummaryTone = 'ok' | 'warn' | 'bad' | 'quiet';

export interface SummaryFact {
  readonly id: string;
  readonly label: string;
  readonly tone: SummaryTone;
}

/**
 * One card, one row: the bold headline first, then a few health facts.
 * A fact never breaks inside itself; on a phone the row may wrap BETWEEN
 * facts, because there is no width that holds them all.
 */
export function SummaryLine({
  headline,
  facts,
  label,
  testId,
}: {
  headline: string;
  facts: readonly SummaryFact[];
  /** What the line sums up, for a screen reader. */
  label: string;
  testId?: string;
}) {
  return (
    <section className="nx-summary-line" aria-label={label} data-testid={testId}>
      <strong className="nx-summary-line__headline">{headline}</strong>
      {facts.map((f) => (
        <span
          key={f.id}
          className={`nx-summary-line__fact nx-summary-line__fact--${f.tone}`}
          data-testid={testId ? `${testId}-${f.id}` : undefined}
        >
          <span className="nx-summary-line__dot" aria-hidden="true" />
          {f.label}
        </span>
      ))}
    </section>
  );
}

export type AttentionSeverity = 'critical' | 'warning' | 'info';

export interface AttentionAction {
  readonly label: string;
  readonly onClick: () => void;
  readonly testId?: string;
}

export interface AttentionItem {
  readonly id: string;
  readonly severity: AttentionSeverity;
  /** One bold sentence. */
  readonly title: string;
  /** One quiet sentence. */
  readonly detail?: string;
  /** The single action that fixes it, for someone who may take it. */
  readonly action?: AttentionAction;
  readonly onDetails?: () => void;
}

/** Nothing at all when nothing needs attention: no empty heading. */
export function AttentionStrip({ items }: { items: readonly AttentionItem[] }) {
  if (items.length === 0) return null;
  return (
    <section className="nx-needs-strip" aria-label="Needs attention" data-testid="attention-strip">
      <h2 className="nx-needs-strip__heading">Needs attention</h2>
      <ul className="nx-needs-strip__list">
        {items.map((item) => (
          <li
            key={item.id}
            className={`nx-needs-card nx-needs-card--${item.severity}`}
            data-testid={`attention-${item.id}`}
          >
            <div className="nx-needs-card__text">
              <strong className="nx-needs-card__title">{item.title}</strong>
              {item.detail && <span className="nx-needs-card__detail">{item.detail}</span>}
            </div>
            <div className="nx-needs-card__actions">
              {item.action && (
                <button
                  type="button"
                  className="nx-screen-btn nx-screen-btn--primary"
                  onClick={item.action.onClick}
                  data-testid={item.action.testId}
                >
                  {item.action.label}
                </button>
              )}
              {item.onDetails && (
                <button type="button" className="nx-screen-btn" onClick={item.onDetails}>
                  Details
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface ScreenTab<Id extends string> {
  readonly id: Id;
  readonly label: string;
  readonly count?: number;
}

/** Tabs with a count pill. On a phone the CSS makes them a segmented
 *  control with 44 px targets. Left and Right move between them. */
export function ScreenTabs<Id extends string>({
  tabs,
  active,
  onChange,
  label,
  testIdPrefix,
}: {
  tabs: readonly ScreenTab<Id>[];
  active: Id;
  onChange: (id: Id) => void;
  label: string;
  testIdPrefix: string;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? 1 : -1;
    const next = (index + step + tabs.length) % tabs.length;
    onChange(tabs[next]!.id);
    barRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  }
  return (
    <div ref={barRef} className="nx-screen-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, index) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`nx-screen-tabs__tab${selected ? ' nx-screen-tabs__tab--active' : ''}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            data-testid={`${testIdPrefix}-${tab.id}`}
          >
            {tab.label}
            {tab.count !== undefined && <span className="nx-screen-tabs__count">{tab.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The search box a tab owns, with its filters beside it. */
export function TableToolbar({
  search,
  onSearch,
  searchLabel,
  children,
}: {
  search: string;
  onSearch: (value: string) => void;
  searchLabel: string;
  children?: ReactNode;
}) {
  return (
    <div className="nx-table-toolbar">
      <input
        type="search"
        className="nx-table-toolbar__search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={searchLabel}
        aria-label={searchLabel}
      />
      {children}
    </div>
  );
}

/**
 * Hotel words first, wire word small: a chip with the class word and a
 * 12 px grey line under it with what the device or system reported.
 */
export function StatusCell({
  status,
  label,
  small,
  dashed = false,
  testId,
}: {
  status: StatusKind;
  label: string;
  small?: ReactNode;
  dashed?: boolean;
  testId?: string;
}) {
  return (
    <span className="nx-status-cell" data-testid={testId}>
      <StatusChip status={status} label={label} dashed={dashed} />
      {small && <span className="nx-cell-small">{small}</span>}
    </span>
  );
}

export interface RowMenuItem {
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly testId?: string;
}

/**
 * A row's "⋯". Fixed to the viewport rather than absolute: the table is a
 * scroll box and would clip it. It closes on Escape, on a press elsewhere,
 * and on scroll, when it would otherwise be left floating over another row.
 */
export function RowMenu({
  label,
  items,
  testId,
}: {
  label: string;
  items: readonly RowMenuItem[];
  testId?: string;
}) {
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const open = at !== null;
  useEffect(() => {
    if (!open) return;
    const close = () => setAt(null);
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      buttonRef.current?.focus();
    };
    const onPress = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPress);
    window.addEventListener('scroll', close, true);
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPress);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);
  if (items.length === 0) return null;
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="nx-screen-btn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        onClick={(e) => {
          if (open) {
            setAt(null);
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();
          setAt({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        }}
      >
        ⋯
      </button>
      {at && (
        <div ref={menuRef} className="nx-row-menu" role="menu" style={at}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="nx-row-menu__item"
              disabled={item.disabled}
              data-testid={item.testId}
              onClick={() => {
                setAt(null);
                item.onClick();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
